import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Tab, Tabs, Grid, Button, TextField,
  Alert, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, IconButton, Tooltip, Switch, FormControlLabel, Divider, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { Refresh, PlayArrow, Email, Edit, Save, Cancel, CheckCircle, PersonAdd, HowToReg, Block, DeleteForever } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { adminApi, usersApi } from '../../services/api';
import FieldVisibilityTab from './FieldVisibilityTab';
import SignatoriesSettings from './SignatoriesSettings';

function TabPanel({ value, index, children }: any) {
  return value === index ? <Box pt={2}>{children}</Box> : null;
}

export default function AdminPage() {
  const [tab, setTab] = useState(0);
  const [settings, setSettings] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [roleDialog, setRoleDialog] = useState<any>(null);
  const [newRole, setNewRole] = useState('');
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ display_name: '', email: '', role: 'viewer', department: '', job_title: '' });
  const [addUserSendInvite, setAddUserSendInvite] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState<any>(null);
  const [approveDialog, setApproveDialog] = useState<any>(null);
  const [approveSendInvite, setApproveSendInvite] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [settingsRes, usersRes, statsRes] = await Promise.all([
        adminApi.getSettings(),
        usersApi.list(),
        adminApi.getStats()
      ]);
      setSettings(settingsRes.data);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch {
      setError('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSaveSetting = async (key: string) => {
    try {
      await adminApi.updateSetting(key, editingValue);
      setSettings(prev => prev.map(s => s.key === key ? { ...s, value: editingValue } : s));
      setEditingKey(null);
      setSuccess('Setting updated');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to update setting');
    }
  };

  const handleTriggerReminders = async () => {
    try {
      await adminApi.triggerReminders();
      setSuccess('Reminder check triggered successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to trigger reminders');
    }
  };

  const handleTestEmail = async () => {
    try {
      const res = await adminApi.testEmail();
      setSuccess(res.data.success ? 'Email connection successful' : `Email test failed: ${res.data.error}`);
      setTimeout(() => setSuccess(null), 5000);
    } catch {
      setError('Email test failed');
    }
  };

  const handleUpdateRole = async () => {
    if (!roleDialog || !newRole) return;
    try {
      await usersApi.updateRole(roleDialog.id, newRole);
      setUsers(prev => prev.map(u => u.id === roleDialog.id ? { ...u, role: newRole } : u));
      setRoleDialog(null);
      setSuccess('Role updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to update role');
    }
  };

  const handleToggleUser = async (userId: string, isActive: boolean) => {
    try {
      await usersApi.updateStatus(userId, !isActive);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: isActive ? 0 : 1 } : u));
      setSuccess(`User ${isActive ? 'deactivated' : 'activated'}`);
      setTimeout(() => setSuccess(null), 2000);
    } catch {
      setError('Failed to update user status');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog) return;
    try {
      await usersApi.delete(deleteDialog.id);
      setUsers(prev => prev.filter(u => u.id !== deleteDialog.id));
      setDeleteDialog(null);
      setSuccess('User deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to delete user');
    }
  };

  const handleApproveUser = async (userId: string, approve: boolean) => {
    if (approve) {
      // Show dialog to ask about invite email
      const user = users.find(u => u.id === userId);
      setApproveSendInvite(true);
      setApproveDialog(user);
      return;
    }
    // Revoke approval instantly — no email needed
    try {
      await usersApi.approve(userId, false, false);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_approved: 0 } : u));
      setSuccess('Approval revoked');
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to revoke approval');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.display_name || !newUser.email) return;
    try {
      const res = await usersApi.create({ ...newUser, send_invite: addUserSendInvite });
      setUsers(prev => [...prev, { ...res.data, owned_contracts: 0, assigned_obligations: 0 }]);
      setAddUserOpen(false);
      setNewUser({ display_name: '', email: '', role: 'viewer', department: '', job_title: '' });
      setAddUserSendInvite(true);
      setSuccess(`User created${addUserSendInvite ? ' — invitation email sent' : ''}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.errors?.map((e: any) => e.msg).join(', ') || 'Failed to create user');
    }
  };

  const handleApproveConfirm = async () => {
    if (!approveDialog) return;
    try {
      await usersApi.approve(approveDialog.id, true, approveSendInvite);
      setUsers(prev => prev.map(u => u.id === approveDialog.id ? { ...u, is_approved: 1 } : u));
      setApproveDialog(null);
      setSuccess(`User approved${approveSendInvite ? ' — invitation email sent' : ''}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch {
      setError('Failed to approve user');
    }
  };

  const formatDate = (d?: string) => d ? format(parseISO(d), 'MMM d, yyyy h:mm a') : 'Never';

  const roleColor: Record<string, any> = { admin: 'error', manager: 'warning', editor: 'primary', viewer: 'default' };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Administration</Typography>
        <Button startIcon={<Refresh />} onClick={load} disabled={loading} variant="outlined" size="small">Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* System Stats */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          {Object.entries(stats).map(([key, value]) => (
            <Grid item xs={6} sm={4} md={3} key={key}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">{key.replace(/_/g, ' ')}</Typography>
                  <Typography variant="h6" fontWeight={600}>{String(value)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Users" />
          <Tab label="System Settings" />
          <Tab label="Reminders & Email" />
          <Tab label="Field Visibility" />
        </Tabs>

        <CardContent>
          {/* Users */}
          <TabPanel value={tab} index={0}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box flex={1}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Users are automatically created on first login and must be approved before they can access COMP.
                </Typography>
                {users.filter(u => !u.is_approved).length > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <strong>{users.filter(u => !u.is_approved).length} user{users.filter(u => !u.is_approved).length !== 1 ? 's' : ''} pending approval</strong> — review and approve below.
                  </Alert>
                )}
                <Alert severity="info" sx={{ mt: 1 }}>
                  Role levels: <strong>Admin</strong> (full access) → <strong>Manager</strong> (manage contracts/users) → <strong>Editor</strong> (create/edit contracts) → <strong>Viewer</strong> (read-only)
                </Alert>
              </Box>
              <Button variant="contained" startIcon={<PersonAdd />} onClick={() => setAddUserOpen(true)} sx={{ ml: 2, whiteSpace: 'nowrap' }}>
                Add User
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Contracts</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell><Typography variant="body2" fontWeight={500}>{user.display_name}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{user.email}</Typography></TableCell>
                      <TableCell>{user.department || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          color={roleColor[user.role] || 'default'}
                          size="small"
                          onClick={() => { setRoleDialog(user); setNewRole(user.role); }}
                          sx={{ cursor: 'pointer', textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {!user.is_approved
                            ? <Chip label="Pending Approval" color="warning" size="small" />
                            : <Chip label={user.is_active ? 'Active' : 'Inactive'} color={user.is_active ? 'success' : 'default'} size="small" />
                          }
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="caption">{formatDate(user.last_login_at)}</Typography></TableCell>
                      <TableCell>{user.owned_contracts || 0}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {!user.is_approved ? (
                            <Tooltip title="Approve access">
                              <IconButton size="small" color="success" onClick={() => handleApproveUser(user.id, true)}>
                                <HowToReg fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <>
                              <Tooltip title={user.is_active ? 'Deactivate' : 'Activate'}>
                                <Switch
                                  size="small"
                                  checked={!!user.is_active}
                                  onChange={() => handleToggleUser(user.id, !!user.is_active)}
                                />
                              </Tooltip>
                              <Tooltip title="Revoke approval">
                                <IconButton size="small" color="warning" onClick={() => handleApproveUser(user.id, false)}>
                                  <Block fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip title="Delete user permanently">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteDialog(user)}
                                disabled={user.id === users.find(u => u.role === 'admin' && u.is_approved)?.id}
                              >
                                <DeleteForever fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Settings */}
          <TabPanel value={tab} index={1}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Setting</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settings.map((setting) => (
                    <TableRow key={setting.key}>
                      <TableCell><Typography variant="body2" fontFamily="monospace">{setting.key}</Typography></TableCell>
                      <TableCell>
                        {editingKey === setting.key ? (
                          <TextField
                            size="small" value={editingValue}
                            onChange={e => setEditingValue(e.target.value)}
                            sx={{ minWidth: 200 }}
                          />
                        ) : (
                          <Typography variant="body2">{setting.value}</Typography>
                        )}
                      </TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{setting.description}</Typography></TableCell>
                      <TableCell>
                        {editingKey === setting.key ? (
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Save"><IconButton size="small" color="success" onClick={() => handleSaveSetting(setting.key)}><Save fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Cancel"><IconButton size="small" onClick={() => setEditingKey(null)}><Cancel fontSize="small" /></IconButton></Tooltip>
                          </Box>
                        ) : (
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditingKey(setting.key); setEditingValue(setting.value); }}><Edit fontSize="small" /></IconButton></Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 3 }}>
              <SignatoriesSettings />
            </Box>
          </TabPanel>

          {/* Reminders & Email */}
          <TabPanel value={tab} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Reminder Engine</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  The reminder engine runs daily at 7:00 AM to check for upcoming contract expirations, obligation due dates, and renewal notice windows. You can manually trigger a check below.
                </Typography>
                <Button
                  startIcon={<PlayArrow />}
                  variant="contained"
                  onClick={handleTriggerReminders}
                >
                  Trigger Reminder Check Now
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Email Configuration</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Configure SMTP settings in the <code>.env</code> file. Test the connection below.
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button startIcon={<Email />} variant="outlined" onClick={handleTestEmail}>
                    Test Email Connection
                  </Button>
                </Box>
                <Alert severity="info" sx={{ mt: 2 }}>
                  Configure email in backend <code>.env</code>: SMTP_HOST, SMTP_USER, SMTP_PASS, SMTP_FROM
                </Alert>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Field Visibility */}
          <TabPanel value={tab} index={3}>
            <FieldVisibilityTab />
          </TabPanel>
        </CardContent>
      </Card>

      {/* Role Dialog */}
      <Dialog open={!!roleDialog} onClose={() => setRoleDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Role for {roleDialog?.display_name}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Role</InputLabel>
            <Select value={newRole} label="Role" onChange={e => setNewRole(e.target.value)}>
              {['admin', 'manager', 'editor', 'viewer'].map(r => (
                <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {newRole === 'admin' && 'Admin users have full access to all features including user management.'}
            {newRole === 'manager' && 'Managers can view all contracts and manage obligations.'}
            {newRole === 'editor' && 'Editors can create and edit contracts and obligations.'}
            {newRole === 'viewer' && 'Viewers have read-only access to their department contracts.'}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setRoleDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateRole}>Update Role</Button>
        </DialogActions>
      </Dialog>

      {/* Approve User Dialog */}
      <Dialog open={!!approveDialog} onClose={() => setApproveDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve {approveDialog?.display_name}?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            This will grant <strong>{approveDialog?.display_name}</strong> ({approveDialog?.email}) access to COMP with <strong>{approveDialog?.role}</strong> role.
          </Typography>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={approveSendInvite} onChange={e => setApproveSendInvite(e.target.checked)} />}
            label={<Typography variant="body2">Send invitation email to {approveDialog?.email}</Typography>}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setApproveDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" startIcon={<HowToReg />} onClick={handleApproveConfirm}>
            Approve Access
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete User Permanently?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This cannot be undone. The user record will be permanently removed.
          </Alert>
          <Typography>
            Are you sure you want to delete <strong>{deleteDialog?.display_name}</strong> ({deleteDialog?.email})?
          </Typography>
          {deleteDialog?.owned_contracts > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This user owns {deleteDialog.owned_contracts} contract{deleteDialog.owned_contracts !== 1 ? 's' : ''}. Those contracts will remain but will have no owner.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" startIcon={<DeleteForever />} onClick={handleDeleteUser}>
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={addUserOpen} onClose={() => setAddUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                required fullWidth label="Display Name" value={newUser.display_name}
                onChange={e => setNewUser(prev => ({ ...prev, display_name: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required fullWidth label="Email" type="email" value={newUser.email}
                onChange={e => setNewUser(prev => ({ ...prev, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select value={newUser.role} label="Role" onChange={e => setNewUser(prev => ({ ...prev, role: e.target.value }))}>
                  {['admin', 'manager', 'editor', 'viewer'].map(r => (
                    <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Department" value={newUser.department}
                onChange={e => setNewUser(prev => ({ ...prev, department: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Job Title" value={newUser.job_title}
                onChange={e => setNewUser(prev => ({ ...prev, job_title: e.target.value }))}
              />
            </Grid>
          </Grid>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={addUserSendInvite} onChange={e => setAddUserSendInvite(e.target.checked)} />}
            label={<Typography variant="body2">Send invitation email to user</Typography>}
          />
          <Alert severity="info" sx={{ mt: 1 }}>
            The user will be able to log in with their Microsoft Entra ID account matching this email address.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setAddUserOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={handleCreateUser}
            disabled={!newUser.display_name || !newUser.email}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
